import { Injectable } from '@angular/core';
import { NotificationService, EntitiesService, TransactionsService, PoliciesService } from 'src/app/shared/services'; // TODO: Migrate locally.
import { ModalService } from 'src/app/shared/modules/modal'; // TODO: Migrate locally.
import { AppService } from '../../services/app/app.service';
import { Entity, ReportDetailsByJurisdiction } from 'src/app/shared/models'; // TODO: Migrate locally.
import { prefix, REPORT_TYPES } from '../../constants';
import { Router } from '@angular/router';
import { ComplianceCentralService } from '../../services';
import { Policies } from '../../../shared/constants';

@Injectable({
  providedIn: 'root'
})
export class EntityDirectoryService {
  _activeTransactionPair;
  activeTransactionEntity;
  private _entityId: string;
  entities: Entity[] = [];
  entitiesById;
  entitiesExistPromise; // Promise that resolves when entities are downloaded.
  entitiesExistResolve; // Resolve function for `entitiesExistPromise`.
  entityReports: ReportDetailsByJurisdiction[] = [];
  filteredEntityReports: ReportDetailsByJurisdiction[] = [];
  _repositoryReportFilter = '';
  rootEntities;
  transactions = [];
  transactionPairs = {};
  transactionPairDropdownItems = [];
  transactionPairMaximums = {};
  transactionPairMetrics = [];
  transactionTypeMetricsByTransactionPair = {};
  currentEntityName: string;
  view;
  getEntitiesAccess: boolean;

  constructor(protected notificationService: NotificationService,
    protected modalService: ModalService,
    protected appService: AppService,
    private _entitiesService: EntitiesService,
    private _transactionsService: TransactionsService,
    private _router: Router,
    private _complianceCentralService: ComplianceCentralService,
    private _policiesService: PoliciesService) {
    this._policiesService.getUserPolicies().subscribe((policies) => {
      this.getEntitiesAccess = policies.some(policy => policy.policyName.includes(Policies.GET_ENTITIES));

      this.getEntities();
      this.entitiesExistPromise = new Promise((resolve) => {
        this.entitiesExistResolve = resolve;
      });
      this.appService.onContainerChanged.subscribe(() => {
        if (this.appService.currentUrl.includes('/entity')) {
          this._router.navigate([`${prefix}/entity-directory`]);
        }
        this.entitiesExistPromise = new Promise((resolve) => {
          this.entitiesExistResolve = resolve;
        });
        this.getEntities();
      });
    });

  }

  get entityId(): string {
    return this._entityId;
  }

  /**
   * When this is set, also ensure that dashboard, repository and transactions
   * data is available.
   */
  set entityId(value: string) {
    if (value !== this._entityId) {
      this._entityId = value;
      if (this._entityId !== null) {
        this.entityReports = [];
        this.getReports(this._entityId);
        this.transactions = [];
        this.getTransactions().subscribe((transactions) => {
          this.transactions = transactions.data;
        });
        this.getTransactionTypeMetricsByTransactionPair(this._entityId);
      }
    }
  }

  get repositoryReportFilter() {
    return this._repositoryReportFilter;
  }

  set repositoryReportFilter(value) {
    this._repositoryReportFilter = value;
    // Note that filterEntityreports mere grabs from the result of `getReports()`
    // which populates `entityReports`.  Note that `getReports()` is also called
    // implicitly when `entityId` is set (which is when the route is changed).
    this.filteredEntityReports = this.filterEntityReports();
  }

  get activeTransactionPair() {
    return this._activeTransactionPair;
  }

  set activeTransactionPair(value) {
    this._activeTransactionPair = value;
    if (this.transactionTypeMetricsByTransactionPair[value]) {
      this.transactionPairMetrics = this
        .transactionTypeMetricsByTransactionPair[value].metrics;
      this.activeTransactionEntity = this.getActiveTransactionEntity();
    } else {
      this.transactionPairMetrics = [];
    }
  }

  initTransactions(): void {
    this.entityId = null;
    this.transactions = [];
    this.transactionPairs = {};
    this.transactionPairDropdownItems = [];
    this.transactionPairMaximums = {};
    this.transactionPairMetrics = [];
    this.transactionTypeMetricsByTransactionPair = {};
    this.filteredEntityReports = [];
  }
  /**
   * Changes the current transaction parties.
   */
  changeTransactionParty({ dest, src, value }) {
    if (value) {
      this.activeTransactionPair = value;
    } else {
      if (this.transactionTypeMetricsByTransactionPair[`${dest} - ${src}`]) {
        this.activeTransactionPair = `${dest} - ${src}`;
      } else {
        this.activeTransactionPair = `${src} - ${dest}`;
      }
    }
    this.transactionPairsToDropdown();
  }

  /**
   * Given a recently changed transaction pair, now find the transactee to
   * match the current entity (defined by the URL typically).
   * @todo Make this a little less confusing because right now it is confusing.
   */
  getActiveTransactionEntity() {
    const transaction =
      this.transactionTypeMetricsByTransactionPair[this.activeTransactionPair]
        .transactionEntities;
    if (Number(transaction.src.entity.entityId) === Number(this.entityId)) {
      return transaction.dest.entity;
    } else {
      return transaction.src.entity;
    }
  }

  /**
   * Given some country ID, get the name.
   * @param id The domicile ID.
   * @deprecated This was used initially if the country was used in the routing,
   * but alas it is not.
   */
  getDomicileName(id) {
    if (!id || !this.entities) {
      return '';
    }
    const entity = this.entities.find(({ domicile }) => {
      if (domicile.countryId === parseInt(id, 10)) {
        return true;
      }
    });
    if (!entity) {
      return '';
    }
    return entity.domicile.name;
  }

  /**
   * Gets an entity by its ID.  As a fallback, utilize the current entityId
   * stored within the service.  This allows the API to be called
   * programmatically or implicitly.
   * @param id Entity ID.
   * @todo This should really draw from a cache instead of making a request.
   */
  getEntityById(id?) {
    if (!id) {
      id = this.entityId;
    }
    if (!id) {
      return { name: '', code: '' };
    }
    return this.entities.find((entity) => {
      return entity.entityId === parseInt(id, 10);
    }) || { name: '', code: '' };
  }

  /**
   * Gets the entities.  Usually this query is run for a given container and a
   * given domicile ID.
   *
   * This is called when the container is changed or when the route is hit.
   */
  private getEntities(): void {
    this.entities = [];

    if (this.getEntitiesAccess) {
      this._entitiesService.list('').subscribe(({ data }) => {
        this.entities = data;
        this.toEntityHierarchy();
        this.entitiesExistResolve();
      });
    }
  }

  /**
   * Gets the first non-detail page in the routing history table.  This is used
   * to enable the 'back' button within the UI.
   */
  public getFirstNonDetailPage() {
    return this.appService.navigationHistory.reverse().find((url) => {
      if (!url.startsWith(`/${prefix}/entity/`)) {
        return true;
      }
      return false;
    });
  }

  /**
   * Gets the reports for the entity.
   * @param entityId The numerical entity ID.  On the entity detail page this
   * is the numerical entity ID found in the route.
   * @todo Refactor, ideally reducing the number of network calls.
   */
  public getReports(entityId): Promise<ReportDetailsByJurisdiction[]> {
    return new Promise(async (resolve) => {
      await this.entitiesExistPromise;
      try {
        const entity = this.getEntityById(entityId) as any;
        const reports = await this.appService.getReports(entity.domicile.countryId, REPORT_TYPES.localFile);
        this.entityReports = reports as ReportDetailsByJurisdiction[];
      } catch (err) {}
      // Note that filterEntityreports mere grabs from the result of `getReports()`
      // which populates `entityReports`.  Note that `getReports()` is also called
      // implicitly when `entityId` is set (which is when the route is changed).
      this.filteredEntityReports = this.filterEntityReports();
      resolve(this.entityReports);
    });
  }

  /**
   * Return all of the pairs and their associated metrics.  The only difference
   * between the returned data here and the `transactionTypeMetricsByTransactionPair`
   * is that direction does not matter, and the net value relative to the
   * `entityId` is returned in the metrics.
   *
   * @todo The semantics of this function may need to change, since volume was
   * determined as the only item of interest, not directionality.  However, this
   * function still does a decent job of consolidating records regardless of
   * directionality and this may be wanted for reuse in the left-hand display of
   * the dashboard.
   */
  public getTransactionPairs(entityId) {
    this.transactionPairs = {};
    for (let pairLabel in this.transactionTypeMetricsByTransactionPair) {
      const pair = this.transactionTypeMetricsByTransactionPair[pairLabel];
      let name = pair.transactionEntities.src.entity.code;
      let entity = pair.transactionEntities.src.entity;
      if (Number(pair.transactionEntities.src.entity.entityId) === Number(entityId)) {
        name = pair.transactionEntities.dest.entity.code;
        entity = pair.transactionEntities.dest.entity;
      }
      if (!this.transactionPairs[name]) {
        this.transactionPairs[name] = {
          metrics: {},
          entity
        };
      }
      for (let metricName in pair.metrics) {
        let value = pair.metrics[metricName];
        if (!this.transactionPairs[name].metrics[metricName]) {
          this.transactionPairs[name].metrics[metricName] = 0;
        }
        this.transactionPairs[name].metrics[metricName] += value;
      }
    }
  }

  /**
   * Gets the transactions given an entity ID.  This is used on the transactions
   * page.
   * @param entityId The numerical entity ID.  On the entity detail page this
   * is the numerical entity ID found in the route.
   */
  public getTransactions() {
    return this._entitiesService.getEntityTransactions(Number(this._entityId), '');
  }

  /**
   * Generates the transaction categories for display on the entity dashboard
   * page.  This is a list of transaction pairs, i.e. `source - destination`
   * and their associated aggregate transaction values, sorted by transaction
   * type.
   *
   * This is used on the dashboard page.
   * @param entityId
   */
  public getTransactionTypeMetricsByTransactionPair(entityId): void {
    this._transactionsService.list().subscribe(({ result }) => {
      this.transactionTypeMetricsByTransactionPair = {};
      // TODO: Not 100% sure this logic will work correctly across all data formats!
      result.transactions.forEach((trans) => {
        const shouldFilter = trans.legalEntityTransactions
          .reduce((acc, entTrans) => {
            // If a single entity lacks the correct entity ID, filter.
            if (entTrans.entity.entityId === parseInt(entityId, 10)) {
              this.currentEntityName = entTrans.entity.name;
              return false;
            }
            return acc;
          }, true);
        if (shouldFilter) {
          // Invalid entity ID.
          return;
        }

        const src: any = {};
        const dest: any = {};
        trans.legalEntityTransactions.forEach((entTrans) => {
          if (entTrans.isSource) {
            Object.assign(src, entTrans);
          } else {
            Object.assign(dest, entTrans);
          }
        });

        const label = `${src.entity.code} - ${dest.entity.code}`;
        const pairedTradingLablel = `${dest.entity.code} - ${src.entity.code}`;
        const isTradingPairExist = this.transactionTypeMetricsByTransactionPair[label] || this.transactionTypeMetricsByTransactionPair[pairedTradingLablel];
        if (!isTradingPairExist) {
          // TODO: For now, these metrics are hard-coded, but this might be
          // wanted to change in future???  Localization needed?
          this.transactionTypeMetricsByTransactionPair[label] = {
            metrics: {
              'Intangible Goods': 0,
              'Tangible Goods': 0,
              'Services': 0,
              'Loans': 0
            },
            transactionEntities: {
              src,
              dest
            },
            transaction: trans
          };
        }

        let appliedLabel: string;
        const isUsingTransactionLabel = this.transactionTypeMetricsByTransactionPair[label];

        if (isUsingTransactionLabel) {
          appliedLabel = label;
        } else {
          const sourceEntityName = pairedTradingLablel.split(' - ')[0];
          const isLabelStartWithCurrentEntityName = sourceEntityName === this.currentEntityName;
          if (!isLabelStartWithCurrentEntityName) {
            this.consolidateTradingPaireEntities(label, pairedTradingLablel);
            appliedLabel = label;
          } else {
            appliedLabel = pairedTradingLablel;
          }
        }

        const transactionType = trans.transactionType.name;
        if (!this.transactionTypeMetricsByTransactionPair[appliedLabel]
          .metrics[transactionType]) {
          this.transactionTypeMetricsByTransactionPair[appliedLabel]
            .metrics[transactionType] = 0;
        }
        this.transactionTypeMetricsByTransactionPair[appliedLabel]
          .metrics[transactionType] += parseFloat(trans.value);
      });

      this.activeTransactionPair = Object.keys(this
        .transactionTypeMetricsByTransactionPair)[0];
      this.getTransactionTypeMetricMaximums();
      this.getTransactionPairs(entityId);
      this.transactionPairsToDropdown();
    });
  }

  /**
   * For the given transaction pairs, find the maximum value for each
   * transaction type.  This assists in drawing the charts such that the metric
   * appears scaled to the other displayed metrics.  It gives the user an easy
   * feedback visually of just how much one metric category represents all
   * entity transactions.
   */
  getTransactionTypeMetricMaximums() {
    const maximums = {};
    for (let pair in this.transactionTypeMetricsByTransactionPair) {
      const { metrics } = this.transactionTypeMetricsByTransactionPair[pair];
      let sum = 0;
      for (let type in metrics) {
        sum += metrics[type];
      }
      maximums[pair] = sum;
    }

    this.transactionPairMaximums = maximums;
  }

  /**
   * Navigates to the first non-detail page.  This relies on the navigation
   * history provided by the AppService.  This is used on the entity detail
   * page for the back button.
   */
  public gotoFirstNonDetailPage() {
    // TODO: Finish me.
    const toPage = this.getFirstNonDetailPage();
    if (toPage) {
      this._router.navigate([toPage]);
    } else {
      this._router.navigate([`${prefix}/entity-directory`]);
    }
  }

  /**
   * Given the current search filter, return the filtered group of reports.
   * This is used on the entail detail, repository page.
   */
  filterEntityReports() {
    return this.entityReports.filter(({ reportName, localEntityName, tradingPartnerName }) => {
      const regexp = new RegExp(`.*${this._repositoryReportFilter}.*`, 'ig');
      return (reportName.match(regexp) ||
        localEntityName.match(regexp) ||
        tradingPartnerName.match(regexp));
    });
  }

  /**
   * Converts a recent entities grab into an entity hierarchy which searches
   * for a root node, then returns a D3 formatted hierarchy object.
   *
   * @todo: This is a lengthy function point!!!  Ugh.
   */
  toEntityHierarchy() {
    this.entitiesById = {};
    this.entities.forEach((ent) => {
      if (!this.entitiesById[ent.entityId]) {
        this.entitiesById[ent.entityId] = {
          entity: ent,
          parents: {},
          children: {}
        };
      }
      if (ent.parentEntities) {
        ent.parentEntities.forEach((relatedEnt) => {
          if (relatedEnt.parent) {
            this.entitiesById[ent.entityId].parents[relatedEnt.parent.entityId] = {
              ownership: relatedEnt.ownership,
              entity: relatedEnt.parent.entityId
            };
          }
          if (relatedEnt.child) {
            this.entitiesById[ent.entityId].children[relatedEnt.child.entityId] = {
              ownership: relatedEnt.ownership,
              entity: relatedEnt.child.entityId
            };
          }
        });
      }
    });

    // First pass, ensure references are all there.
    Object.keys(this.entitiesById).forEach((id) => {
      Object.keys(this.entitiesById[id].children).forEach((cid) => {
        if (!this.entitiesById[cid].parents[id]) {
          this.entitiesById[cid].parents[id] = {
            ownership: this.entitiesById[id].children[cid].ownership,
            entity: id
          };
        }
      });
      Object.keys(this.entitiesById[id].parents).forEach((pid) => {
        // TODO - why are there parents that are not in this this.entitiesById
        if (this.entitiesById[pid] && !this.entitiesById[pid].children[id]) {
          this.entitiesById[pid].children[id] = {
            ownership: this.entitiesById[id].parents[pid].ownership,
            entity: id
          };
        }
      });
    });

    this.rootEntities = [];
    // Second pass, extract the root entities.
    Object.keys(this.entitiesById).forEach((id) => {
      if (Object.keys(this.entitiesById[id].parents).length === 0) {
        this.rootEntities.push(this.entitiesById[id]);
      }
    });

    // On the next pass, devise a structure desired by D3.
    Object.keys(this.entitiesById).forEach((id) => {
      const ent = this.entitiesById[id];
      // TODO: Is this desired???
      ent.name = /* ent.entity.name || */ ent.entity.code;
      const children = [];
      Object.keys(ent.children).forEach((cid) => {
        ent.children[cid].entity = this.entitiesById[cid];
        if (cid !== id) {
          children.push(Object.assign(ent.children[cid].entity, {
            ownership: ent.children[cid].ownership
          }));
        }
      });
      ent.children = children;
      delete ent.parents;
    });

    // This final pass removes circuluar references.
    this.rootEntities.forEach((root) => {
      let traversedEntities = {};
      /**
       * Scrub through the children of the entitiy, and if any previously
       * referenced children exist, then copy the object, and empty the children
       * array to prevent infinite recursion.
       */
      const scrub = (ent) => {
        ent.id = ent.name;
        ent.children.forEach((child, idx) => {
          if (traversedEntities[child.entity.entityId]) {
            ent.children[idx] = Object.assign({}, child);
            ent.children[idx].children = []; // Stub children array.
          } else {
            traversedEntities[child.entity.entityId] = true;
          }
        });
        ent.children.forEach((child) => {
          scrub(child);
        });
      };
      traversedEntities[root.entity.entityId] = true;
      scrub(root);
    });
  }

  /**
   * This converts the transaction pairs into a dropdown menu format.  This is
   * used on the dashboard page.
   */
  private transactionPairsToDropdown() {
    const items = Object.keys(this.transactionTypeMetricsByTransactionPair)
      .map((key) => ({
        name: key,
        selected: key === this.activeTransactionPair,
        value: key
      }));

    this.transactionPairDropdownItems = items;
    return items;
  }

  private consolidateTradingPaireEntities(label, pairedLablel) {
    this.transactionTypeMetricsByTransactionPair[label] = this.transactionTypeMetricsByTransactionPair[pairedLablel];
    delete this.transactionTypeMetricsByTransactionPair[pairedLablel];
  }

  downloadReport(report): void {
    this._complianceCentralService.getSignedDownloadURL(report.reportInstanceBinaryPath, report.localEntityName);
  }
}
